import React, { useState, useEffect } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { StaticImageData } from 'next/image'
import Link from 'next/link'
import {
  InfoWrapper,
  DesktopHeroWrapper,
  PricePerUnit,
  Spacer,
  Description,
  HeroHeader,
  ItemCounterWrapper,
  Discount,
  PriceWrapper,
  BadgeWrapper,
  BadgeOutline,
} from './Hero.styles'
import {
  ContentContainer,
  MobileWrapper,
  DesktopWrapper,
} from 'features/components/ProductDetailHero/ProductDetailHero.styles'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { HeroImage } from 'features/components/ProductDetailHero/components/HeroImage'
import { InformationCollapse } from 'features/components/ProductDetailHero/components/InformationCollapse'
import { ProductDetailHero } from 'features/components/ProductDetailHero'
import { ItemCounter } from 'features/components/ProductDetailHero/components/ItemCounter/ItemCounter'
import { CtaButton } from 'components/CtaButton'
import Trans from 'next-translate/Trans'
import { HeroTitle } from 'features/components/ProductDetailHero/components/HeroHeader/HeroHeader.styles'
import { Product } from 'models/Product'

type HeroProps = {
  thumbnail: StaticImageData
  box: 'big-box' | 'classic-box' | 'small-box'
  product: Product | undefined
  onAdd: (productId: string, quantity: number, productName: string) => void
}

export const Hero = ({ thumbnail, onAdd, box, product }: HeroProps) => {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(1)
  
  if (!product) return null

  const pricePerUnit = product.price.subtotal / 100
  const discountedPrice = product.price.total / 100
  const percentOff = Math.round((pricePerUnit - discountedPrice) * 100 / pricePerUnit)

  const infoList = [
    {
      label: t('products:safetyQuality.safety.title'),
      description: t('products:safetyQuality.safety.description'),
    },
    {
      label: t('products:essentials.hero.feedingGuidelines.title'),
      description: t('products:essentials.hero.feedingGuidelines.description'),
    },
    {
      label: t('products:essentials.hero.firstTime.title'),
      description: t('products:essentials.hero.firstTime.description'),
    },
  ]

  const handleAdd = () => setQuantity((prev) => prev + 1)

  const handleRemove = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1)
    }
  }

  return (
    <ProductDetailHero>
      <DesktopHeroWrapper>
        <HeroImage thumbnail={thumbnail} alt={t('products:essentials.hero.thumbnailAlt')} />
        <BadgeOutline>
          <BadgeWrapper>
            <Text align="center" size="h5" color="accent-white" weight="medium">
              <Trans
                components={{ br: <br /> }}
                i18nKey={'products:essentials.hero.badge.title'}
              />
            </Text>
            <Text align="center" size="headline" color="accent-white">
              {t('products:essentials.hero.badge.subtitle')}
            </Text>
          </BadgeWrapper>
        </BadgeOutline>
      </DesktopHeroWrapper>
      <ContentContainer>
        <MobileWrapper>
          <Stack
            css={{ marginBottom: '$5' }}
            width="fullWidth"
            direction="column"
            alignItems="center"
            gap="2"
          >
            <Text size='h5' color="quaternary-200">
              {t('products:essentials.hero.selectSize')}
            </Text>
            <Stack width="fullWidth" justifyContent="center" direction="column" gap="2">
              <Link href="small-box">
                <CtaButton width="regular" type={box === 'small-box' ? 'primary' : 'secondary'}>
                  {t('products:essentials.hero.small-box')}
                </CtaButton>
              </Link>
              <Link href="classic-box">
                <CtaButton width="regular" type={box === 'classic-box' ? 'primary' : 'secondary'}>
                  {t('products:essentials.hero.classic-box')}
                </CtaButton>
              </Link>
              <Link href="big-box">
                <CtaButton width="regular" type={box === 'big-box' ? 'primary' : 'secondary'}>
                  {t('products:essentials.hero.big-box')}
                </CtaButton>
              </Link>
            </Stack>
          </Stack>
        </MobileWrapper>
        <Stack width='fullWidth' direction="column" alignItems="center" gap={{ '@initial': '2', '@bp3': '5' }}>
          <HeroHeader>
            <HeroTitle>{t(`products:essentials.hero.${box}`)}</HeroTitle>
            <Text weight="medium" color="quaternary-200" size='bodyExtraLarge'>
              {t(`products:essentials.hero.subtitle.${box}`)}
            </Text>
            <Description
              align="center"
              size={{'@initial': 'body', '@bp3': 'bodyLarge'}}
              color="quaternary-200"
            >
              <Trans
                i18nKey={`products:essentials.hero.${box}-description`}
                components={{ br: <br /> }}
              />
            </Description>
          </HeroHeader>
          <MobileWrapper>
            <HeroImage thumbnail={thumbnail} alt={t('products:essentials.hero.thumbnailAlt')} />
          </MobileWrapper>
          <DesktopWrapper>
            <Stack width="fullWidth" direction="column" alignItems="center" gap="2">
              <Text size='h5' color="quaternary-200">
                {t('products:essentials.hero.selectSize')}
              </Text>
              <Stack width="fullWidth" justifyContent="center" direction="row" gap="2">
                <Link href="small-box">
                  <CtaButton width="small" type={box === 'small-box' ? 'primary' : 'secondary'}>
                    {t('products:essentials.hero.small-box')}
                  </CtaButton>
                </Link>
                <Link href="classic-box">
                  <CtaButton width="small" type={box === 'classic-box' ? 'primary' : 'secondary'}>
                    {t('products:essentials.hero.classic-box')}
                  </CtaButton>
                </Link>
                <Link href="big-box">
                  <CtaButton width="small" type={box === 'big-box' ? 'primary' : 'secondary'}>
                    {t('products:essentials.hero.big-box')}
                  </CtaButton>
                </Link>
              </Stack>
            </Stack>
          </DesktopWrapper>
          <Stack direction="column" alignItems="center" width='fullWidth'>
            <PriceWrapper>
              <PricePerUnit size='h5' color="quaternary-200">
                ${pricePerUnit} / box
              </PricePerUnit>
            </PriceWrapper>
            <ItemCounterWrapper>
              <ItemCounter counter={quantity} onAdd={handleAdd} onRemove={handleRemove} />
            </ItemCounterWrapper>
            <CtaButton
              onClick={() => onAdd(product.productId, quantity, product.name)}
              type="primary"
              width="large"
            >
              {t('products:fivePound.cta')}
            </CtaButton>
            <Discount>
              {t('products:essentials.hero.save-alert')} <strong>{`$${discountedPrice}`}</strong> ({percentOff}% off)
            </Discount>
          </Stack>
        </Stack>
        <Spacer />
        <InfoWrapper>
          <InformationCollapse infoList={infoList} />
        </InfoWrapper>
      </ContentContainer>
    </ProductDetailHero>
  )
}

Hero.displayName = 'Hero'
